import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { EditorShapeMetadata } from '@/api/types/stowed-element'
import type { ParagraphBlockFontLookup } from '@/lib/text/types/paragraph-block-font-lookup'
import { INSET_MAX, INSET_MIN } from '@/lib/object-effects/sides'
import { isClosedPenPathShape } from './is-closed-pen-path-shape'
import { buildWireParagraphs } from '@/lib/elements/wire-paragraphs'

export function buildEditorShape(
  shape: ShapeData,
  fonts?: ParagraphBlockFontLookup
): EditorShapeMetadata {
  const isClosed = isClosedPenPathShape(shape)
  // Persist the shape-text-frame identity whenever the shape carries paragraphs — even when they
  // are empty. A shape "is a text frame" purely by carrying `paragraphs` (there is no dedicated
  // flag), so gating this on `hasShapeTextContent` dropped the marker for empty frames and they
  // reloaded as plain shapes (WMPCM-8160). The wire `content.text` stays gated on actual content
  // (see `buildShapeTextContent`), so no empty text is written to the NSD, except an empty
  // paragraph that carries an active Drop Cap.
  const sourceParagraphs = shape.paragraphs
  const wireParagraphs = sourceParagraphs?.length
    ? buildWireParagraphs(sourceParagraphs, fonts, true)
    : undefined

  return {
    type: shape.type,
    fill: shape.fill,
    stroke: shape.stroke,
    strokeWidth: shape.strokeWidth,
    opacity: shape.opacity,
    ...(wireParagraphs ? { paragraphs: wireParagraphs } : {}),
    inset:
      typeof shape.inset === 'number'
        ? Math.max(INSET_MIN, Math.min(INSET_MAX, Math.round(shape.inset)))
        : undefined,
    ...(isClosed ? { isClosed: true } : {}),
  }
}
